import {merge} from '#core/utils/config-utils.js';

import {PALETTE_IDS} from '#core/application/modules/ids.js';

import {COLOR, FONT} from './theme.js';
import {getBaseComponentsConfig} from './style/base-components';
import {getDialogConfig} from './style/dialogs';
import {getMenuListsConfigs} from './style/menus-links-lists';
import {getPageHeadersConfigs} from './style/interface';
import {getStaticPagesConfig} from './style/static-pages';
import {getVirtualsConfig} from './style/virtuals';
import {getMatchAccaConfig} from './style/matchacca';
import {getBonusesConfig} from './style/bonuses';
import {getBetHistoryConfig} from './style/bethistory';
import {getRacingConfig} from './style/racing';
import {getAccountConfigs} from './style/account';
import {getCardsConfig} from './style/cards';
import {getMarketsConfig} from './style/markets';
import {getBettingCenterConfig} from './style/betting-center';
import {getSportsContentConfigs} from './style/sports-content';

export function getTokens() {
    const TILE_FONT = Object.assign({}, FONT);
    delete TILE_FONT.FAMILY_FUTURA;
    delete TILE_FONT.WEIGHT_FUTURA_BOLD;

    const custom = {
        [PALETTE_IDS.PROMOTIONS]: {
            BannerPromotionsView: {
                banner: {
                    image: {
                        style: {
                            /*
                            Solverde use 900x265 (3.4 / 1) banners for mobile on Prod at the moment.
                            The promotions.mobileAspectRatio config configured as '3.78 / 1' to fit this aspect ratio.
                            This is because a slide in the swiper takes 90% of available space on mobile: 3.4 / 90% = 3.78.
                            While a single (unstacked) slide has to be '3.4 / 1' because it fits the full page width.
                             */
                            _mobile_aspectRatio: '3.4 / 1',
                        },
                    },
                },
            },
        },
        [PALETTE_IDS.SCOREBOARDS]: {
            Scoreboard: {
                backgroundImages: {
                    motorcycling: 'card-background/scoreboard_bg_motor_sports.png',
                    motorsport: 'card-background/scoreboard_bg_motor_sports.png',
                    formula1: 'card-background/scoreboard_bg_motor_sports.png',
                },
            },
        },
    };
    const palette = {
        [PALETTE_IDS.FONT]: TILE_FONT,
        [PALETTE_IDS.APP_VIEW]: {
            BACK_TO_TOP_BUTTON: {
                TERTIARY_BUTTON_TEXT: {
                    fontWeight: FONT.WEIGHT_BOLD,
                    color: COLOR.BRAND.ACCENT1,
                    textTransform: 'uppercase',
                },
                TERTIARY_BUTTON_ICON_COLOR: COLOR.BRAND.ACCENT1,
            },
            ACCA_BAR_BORDER_COLOR: COLOR.BRAND.BACKGROUND1,
            ACCA_BAR_LABEL_TEXT: {
                fontWeight: FONT.WEIGHT_SEMI_BOLD,
                color: COLOR.BRAND.FOREGROUND1,
            },
            ACCA_BAR_CLOSE_BUTTON: {
                CLOSE_BUTTON_ICON_COLOR: COLOR.BRAND.FOREGROUND1,
            },
        },
        [PALETTE_IDS.MOBILE_NAVBAR]: {
            CONTAINER_BG: COLOR.BRAND.BACKGROUND8,
            LABEL_TEXT: {
                color: COLOR.BRAND.FOREGROUND1,
                textTransform: 'uppercase',
            },
            DOT_NOTIFICATION: {
                DOT_COLOR: COLOR.BRAND.ACCENT1,
            },
        },
        [PALETTE_IDS.SEARCH_RESULT]: {
            SEARCH_RECENT_ITEM_TEXT: COLOR.BRAND.FOREGROUND1,
            SEARCH_RECENT_ITEM_REMOVE_ICON_COLOR: COLOR.BRAND.FOREGROUND2,
            SEARCH_RECENT_ITEM_ICON_COLOR: COLOR.BRAND.FOREGROUND1,

            SEARCH_RESULT_ITEM_ICON_COLOR: COLOR.BRAND.FOREGROUND1,
            SEARCH_RESULT_ITEM_TOURNAMENT_TEXT: COLOR.BRAND.FOREGROUND1,
            SEARCH_RESULT_ITEM_NAME_TEXT: {
                fontWeight: FONT.WEIGHT_SEMI_BOLD,
                color: COLOR.BRAND.FOREGROUND1,
            },
            SEARCH_RESULT_ITEM_STATUS_TEXT: COLOR.BRAND.FOREGROUND1,
        },
        [PALETTE_IDS.SEARCH]: {
            SEARCH_OVERLAY_BG: COLOR.BRAND.BACKGROUND1,
        },
        [PALETTE_IDS.SCOREBOARDS]: {
            ACCENT_COLOR: COLOR.BRAND.ACCENT2,
            SCOREBOARD_CONTAINER_BG: COLOR.BRAND.BACKGROUND4,
            PRIMARY_COLOR: COLOR.BRAND.FOREGROUND3,
            SECONDARY_COLOR: COLOR.BASE.DIVIDER2,
            SEPARATOR_COLOR: COLOR.BASE.DIVIDER2,
            SCORE_TEXT: COLOR.BRAND.CTA1,
            SEGMENT_TEXT: COLOR.BRAND.FOREGROUND1,
            SEGMENT_TOTAL_HEADER_TEXT: COLOR.BRAND.CTA1,

            TIMING_STATUS_LABEL: {
                EVENT_TIMING_LIVE_TEXT: COLOR.BRAND.FOREGROUND3,
                EVENT_TIMING_UPCOMING_TEXT: COLOR.BRAND.FOREGROUND3,
            },
            SEGMENT_TOTAL_TEXT: {
                fontSize: FONT.SIZE_LARGE,
                color: COLOR.BRAND.CTA1,
            },
            PARTICIPANT_NAME_TEXT: {
                color: COLOR.BRAND.FOREGROUND1,
                fontSize: FONT.SIZE_MEDIUM,
            },
        },
        [PALETTE_IDS.SKELETON]: {
            SKELETON_COLOR: COLOR.BRAND.BACKGROUND5,
        },
        [PALETTE_IDS.CHANGED_VALUE_INDICATOR]: {
            CVI_NEW_VALUE_BG: COLOR.SEMANTIC.WARNING2,
            CVI_NEW_VALUE_TEXT: {
                color: `${COLOR.BRAND.FOREGROUND4}|${COLOR.BRAND.FOREGROUND1}`,
                fontWeight: FONT.WEIGHT_SEMI_BOLD,
            },
            CVI_INDICATION_ICON_COLOR: COLOR.BRAND.ACCENT1,
        },
        [PALETTE_IDS.INPUT]: {
            INPUT_BG: COLOR.BRAND.BACKGROUND4,
            INPUT_BORDER_COLOR: COLOR.BRAND.BACKGROUND4,
            INPUT_FOCUSED_BORDER_COLOR: COLOR.BRAND.BACKGROUND4, // made it suitable for search, as it used only there
            INPUT_TEXT: {
                color: COLOR.BRAND.FOREGROUND1,
                fontWeight: FONT.WEIGHT_NORMAL,
            },
            INPUT_LABEL_TEXT: {
                color: COLOR.BRAND.FOREGROUND1,
                fontSize: FONT.SIZE_SMALL,
            },
            INPUT_ERROR_BORDER_COLOR: COLOR.SEMANTIC.ERROR2,
            INPUT_DISABLED_TEXT: COLOR.BRAND.FOREGROUND5,
            INPUT_ICON_COLOR: COLOR.BRAND.FOREGROUND1,
        },
        [PALETTE_IDS.STAKE_INPUT]: {
            STAKE_INPUT_BG: COLOR.BRAND.BACKGROUND4,
            STAKE_INPUT_TEXT: {
                color: COLOR.BRAND.FOREGROUND1,
                fontWeight: FONT.WEIGHT_NORMAL,
            },
            STAKE_INPUT_ERROR_BORDER_COLOR: COLOR.SEMANTIC.ERROR2,
            STAKE_INPUT_DISABLED_TEXT: COLOR.BRAND.FOREGROUND5,
            STAKE_INPUT_BORDER_COLOR: COLOR.RESERVED.TRANSPARENT,
            STAKE_INPUT_FOCUSED_BORDER_COLOR: COLOR.BRAND.ACCENT1,
        },
        [PALETTE_IDS.RANGE_INPUT]: {
            RANGE_TRACK_COLOR: COLOR.BRAND.BACKGROUND5,
            RANGE_TRACK_DISABLED_COLOR: COLOR.BRAND.BACKGROUND5,
            RANGE_TRACK_FILLED_COLOR: COLOR.BRAND.FOREGROUND1,
            RANGE_THUMB_COLOR: COLOR.BRAND.FOREGROUND2,
            RANGE_THUMB_DISABLED_COLOR: COLOR.BRAND.BACKGROUND5,
            RANGE_LABEL_TEXT: COLOR.BRAND.FOREGROUND1,
        },
        [PALETTE_IDS.ERROR_NOTIFICATION]: {
            ERROR_BG: COLOR.SEMANTIC.ERROR2,
            ERROR_BORDER_COLOR: COLOR.SEMANTIC.ERROR2,
        },
        [PALETTE_IDS.WARNING_NOTIFICATION]: {
            WARNING_BG: COLOR.SEMANTIC.WARNING2,
            WARNING_BORDER_COLOR: COLOR.SEMANTIC.WARNING2,
        },
        [PALETTE_IDS.SUCCESS_NOTIFICATION]: {
            SUCCESS_BG: COLOR.SEMANTIC.SUCCESS2,
            SUCCESS_ICON_COLOR: COLOR.RESERVED.BLACK,
            SUCCESS_BORDER_COLOR: COLOR.SEMANTIC.SUCCESS2,
        },
        [PALETTE_IDS.INFO_NOTIFICATION]: {
            INFO_BG: COLOR.BRAND.FOREGROUND7,
            INFO_BORDER_COLOR: COLOR.BRAND.FOREGROUND5,
        },
        [PALETTE_IDS.EMPTY_CONTENT_MESSAGE]: {
            EMPTY_CONTENT_MESSAGE_TEXT: COLOR.BRAND.FOREGROUND1,
        },
        [PALETTE_IDS.SWITCH]: {
            SWITCH_CONTAINER_BG: COLOR.BRAND.BACKGROUND4,
            SWITCH_CONTAINER_BORDER_COLOR: COLOR.BRAND.BACKGROUND4,
            SWITCH_BUTTON_BG: COLOR.BRAND.FOREGROUND5,
            SWITCH_ON_BUTTON_BG: COLOR.BRAND.CTA1,
            SWITCH_ICON_COLOR: COLOR.BRAND.BACKGROUND1,
            SWITCH_OFF_ICON_COLOR: COLOR.BRAND.FOREGROUND1,
            SWITCH_LABEL_TEXT: `${COLOR.BRAND.FOREGROUND4}|${COLOR.BRAND.FOREGROUND1}`,
            SWITCH_DISABLED_CONTAINER_BG: COLOR.BRAND.BACKGROUND6,
            SWITCH_DISABLED_CONTAINER_BORDER_COLOR: COLOR.BRAND.BACKGROUND6,
            SWITCH_DISABLED_BUTTON_BG: COLOR.BRAND.FOREGROUND3,
            SWITCH_DISABLED_LABEL_TEXT: COLOR.BRAND.FOREGROUND3,
        },
        [PALETTE_IDS.BET_SHARING_DIALOG]: {
            BET_SHARING_CONTENT_CONTAINER_BG: COLOR.BRAND.BACKGROUND8,
        },
    };

    const tokens = merge(
        {
            custom,
            palette,
        },
        getBaseComponentsConfig(),
        getAccountConfigs(),
        getMenuListsConfigs(),
        getBetHistoryConfig(),
        getBettingCenterConfig(),
        getBonusesConfig(),
        getCardsConfig(),
        getDialogConfig(),
        getMarketsConfig(),
        getMatchAccaConfig(),
        getPageHeadersConfigs(),
        getRacingConfig(),
        getStaticPagesConfig(),
        getVirtualsConfig(),
        getSportsContentConfigs()
    );

    return tokens;
}
